import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCharacterListAPI } from "../api/user";

type CharacterItem = {
  id: string;
  image: string;
  type: string;
  gender: string;
};

export const resolveCharacterUrlFromList = (
  profileImage?: string | null,
  characters: CharacterItem[] = []
) => {
  if (!profileImage) return null;
  const fileName = profileImage.split("/").pop() || "";
  const match = fileName.match(/^(\d+)/);
  const characterNumber = match?.[1];
  if (!characterNumber) return profileImage;
  const matchedCharacter = characters.find((item) => item.image?.split("/").pop()?.startsWith(characterNumber));
  return matchedCharacter?.image || profileImage;
};

export const getDefaultPortraitByGender = (
  gender?: string | null,
  characters: CharacterItem[] = []
) => {
  if (!characters.length) return null;
  const isFemale = String(gender || "").toLowerCase() === "female";
  const preferredIndex = isFemale ? 5 : 2; // 6th for female, 3rd for male
  return characters[preferredIndex]?.image || characters[0]?.image || null;
};

export const usePortraitCharacterUrl = (profileImage?: string | null) => {
  const { data: characterListData } = useQuery({
    queryKey: ["characterList", "portrait"],
    queryFn: () => getCharacterListAPI({ type: "portrait" }),
    staleTime: 1000 * 60 * 30,
  });

  const characterUrl = useMemo(() => {
    const characters: CharacterItem[] = (characterListData as any)?.data?.characters || [];
    return resolveCharacterUrlFromList(profileImage, characters);
  }, [characterListData, profileImage]);

  return characterUrl;
};

export const useStandingCharacterUrl = (profileImage?: string | null) => {
  const { data: characterListData } = useQuery({
    queryKey: ["characterList", "standing"],
    queryFn: () => getCharacterListAPI({ type: "standing" }),
    staleTime: 1000 * 60 * 30,
  });

  const characterUrl = useMemo(() => {
    const characters: CharacterItem[] = (characterListData as any)?.data?.characters || [];
    return resolveCharacterUrlFromList(profileImage, characters);
  }, [characterListData, profileImage]);

  return characterUrl;
};

export const useSittingCharacterUrl = (profileImage?: string | null) => {
  const { data: characterListData } = useQuery({
    queryKey: ["characterList", "sitting"],
    queryFn: () => getCharacterListAPI({ type: "sitting" }),
    staleTime: 1000 * 60 * 30,
  });

  const characterUrl = useMemo(() => {
    const characters: CharacterItem[] = (characterListData as any)?.data?.characters || [];
    return resolveCharacterUrlFromList(profileImage, characters);
  }, [characterListData, profileImage]);

  return characterUrl;
};
